import React, { useEffect, useState } from "react";
import { useFormStatic} from "./DyanmicFormContext.tsx";
import { useFileApi } from "../../context/ApiContext.tsx";
import { FileDownload } from "../../api/ApiTypes.ts";
import { base64ToArrayBuffer } from "../../filehelper/fileutils.ts";

export const DefaultScope: React.FC = React.memo(() => {
    const {metaData} = useFormStatic()
    const fileApi = useFileApi();
    const [fileData, setFileData] = useState<FileDownload | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [pdfUrl, setPdfUrl] = useState<string | null>(null);

    useEffect(() => {
        const fetchScopeOfWork = async () => {
            if (!metaData?.orderId) {
                setError("Order ID is missing");
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);

                const response = await fileApi.getScopeOfWork(metaData.orderId);

                if (response && response.data) {
                    setFileData(response);
                    const blob = new Blob([base64ToArrayBuffer(response.data)], { type: response.contentType });
                    const url = URL.createObjectURL(blob);
                    setPdfUrl(url);
                } else {
                    setError("Failed to retrieve scope of work document");
                }
            } catch (err) {
                console.error("Error fetching scope of work:", err);
                setError("An error occurred while retrieving the document");
            } finally {
                setLoading(false);
            }
        };

        fetchScopeOfWork();

        return () => {
            if (pdfUrl) {
                URL.revokeObjectURL(pdfUrl);
            }
        };
    }, [metaData?.orderId]);

    if (loading) {
        return (
            <div className="p-8 text-center bg-gray-50 rounded">
                <p>Loading document...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-8 text-center bg-red-50 text-red-600 rounded border border-red-200">
                <p>Error: {error}</p>
            </div>
        );
    }

    if (!fileData || !pdfUrl) {
        return (
            <div className="p-8 text-center bg-gray-50 rounded border border-gray-200">
                <p>No document available</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col w-full font-sans">
            <h3 className="text-lg font-semibold mb-4 text-gray-800">Scope of Work</h3>
            <div className="pdf-wrapper relative shadow-lg rounded-md overflow-hidden">
                {/* Custom header to make it look integrated */}
                <div className="bg-white border-b border-gray-200 p-3 flex justify-between items-center">
                    <div className="flex items-center">
                        <svg className="w-5 h-5 text-red-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                        </svg>
                        <span className="font-medium">Scope of Work Document</span>
                    </div>
                    <a
                        href={pdfUrl}
                        download="scope_of_work.pdf"
                        className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                        Download
                    </a>
                </div>

                {/* Embedded PDF with custom styling */}
                <iframe
                    src={pdfUrl}
                    title="Scope of Work PDF"
                    className="w-full h-96 bg-gray-50"
                    style={{
                        border: "none",
                        borderRadius: "0 0 0.375rem 0.375rem",
                    }}
                />
            </div>
        </div>
    );
});